import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Menu, Bell, Check, CheckCheck } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { useAppStore, useAuthStore } from '@/stores'
import {
  useAuth,
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '@/hooks'
import { useDesktopNotifications } from '@/hooks/use-desktop-notifications'
import { Button } from '@/components/ui/button'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { Notification } from '@/types'

export function Header() {
  const { toggleMobileMenu } = useAppStore()
  const { user, organization } = useAuthStore()
  const { signOut } = useAuth()
  const navigate = useNavigate()

  const { data: notifications = [], isLoading: notifLoading } = useNotifications({ limit: 20 })
  const { data: unreadData } = useUnreadNotificationCount()
  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  // Native desktop notifications for new items (foreground; kiosk-friendly).
  const desktop = useDesktopNotifications(notifications)

  const unreadCount = unreadData?.unread_count ?? 0
  const [notifOpen, setNotifOpen] = useState(false)

  // Where a notification takes you when clicked. work_order_id always wins; then the
  // typed entity; then a fallback by notification_type (e.g. the ISP compliance summary).
  const routeForNotification = (n: Notification): string | null => {
    // To-do notifications deep-link to the to-do's thread page, even when the
    // to-do is linked to a WO (so the assigner can answer the question there),
    // so this must run before the work_order_id shortcut below.
    if (n.entity_type === 'user_todo') return n.entity_id ? `/todos/${n.entity_id}` : '/dashboard'
    // Receipts: the reviewer works them on the dashboard widget / ledger; the
    // crew fixes theirs from My Crew Week. Must run before the WO shortcut.
    if (n.entity_type === 'receipt') {
      return n.notification_type === 'receipt_submitted' ? '/finance/receipts' : '/portal/crew'
    }
    // Missed punch-out flagged for finance: fix it on the timesheet. Before the WO shortcut.
    if (n.entity_type === 'time_entry') return '/timesheets'
    if (n.work_order_id) return `/work-orders/${n.work_order_id}`
    switch (n.entity_type) {
      case 'work_order': return n.entity_id ? `/work-orders/${n.entity_id}` : null
      case 'vendor': return n.entity_id ? `/vendors/${n.entity_id}` : '/vendors'
      case 'isp_compliance': return '/dispatch'
      case 'dispatch_request': return '/dispatch-board'
      case 'prospect':
      case 'prospect_site': return n.entity_id ? `/prospects/${n.entity_id}` : '/prospects'
      case 'client': return n.entity_id ? `/clients/${n.entity_id}` : '/clients'
      case 'equipment': return n.entity_id ? `/equipment/${n.entity_id}` : '/equipment'
    }
    // Fallbacks keyed off the notification's type.
    if (n.notification_type === 'vendor_compliance') return '/dispatch'
    return null
  }

  const handleNotificationClick = (n: Notification) => {
    if (!n.read) markRead.mutate(n.id)
    const to = routeForNotification(n)
    if (to) {
      setNotifOpen(false)
      navigate(to)
    }
  }

  const initials = user?.full_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? 'U'

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-background px-4 lg:px-6">
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={toggleMobileMenu}
      >
        <Menu className="h-5 w-5" />
        <span className="sr-only">Toggle menu</span>
      </Button>

      {/* Mobile logo */}
      <Link to="/dashboard" className="flex items-center gap-2 lg:hidden">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
          <span className="text-sm font-bold text-primary-foreground">C</span>
        </div>
      </Link>

      {/* Spacer — pushes the actions cluster to the right edge on every
          breakpoint. Previously the desktop search box (flex-1) and the mobile
          search button (ml-auto) did this incidentally; both were dead controls
          wired to nothing, so this replaces them. */}
      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold leading-none text-destructive-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
              <span className="sr-only">Notifications</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 p-0">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <div className="text-sm font-semibold">Notifications</div>
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => markAllRead.mutate()}
                  disabled={markAllRead.isPending}
                >
                  <CheckCheck className="mr-1 h-3.5 w-3.5" />
                  Mark all read
                </Button>
              )}
            </div>
            {desktop.supported && desktop.permission === 'default' && (
              <button
                type="button"
                onClick={() => desktop.requestPermission()}
                className="flex w-full items-center gap-2 border-b bg-indigo-50/60 px-4 py-2 text-left text-xs font-medium text-indigo-700 hover:bg-indigo-100/60 dark:bg-indigo-950/30 dark:text-indigo-300"
              >
                <Bell className="h-3.5 w-3.5" /> Enable desktop notifications
              </button>
            )}
            <ScrollArea className="max-h-[400px]">
              {notifLoading ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading...
                </div>
              ) : notifications.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No notifications
                </div>
              ) : (
                <ul className="divide-y">
                  {notifications.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        onClick={() => handleNotificationClick(n)}
                        className={cn(
                          'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
                          !n.read && 'bg-muted/30',
                        )}
                      >
                        <div
                          className={cn(
                            'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                            n.read ? 'bg-transparent' : 'bg-primary',
                          )}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <p className="truncate text-sm font-medium">{n.title}</p>
                            {!n.read && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  markRead.mutate(n.id)
                                }}
                                className="shrink-0 text-muted-foreground hover:text-foreground"
                                aria-label="Mark as read"
                              >
                                <Check className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                          {n.message && (
                            <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                              {n.message}
                            </p>
                          )}
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </ScrollArea>
            <div className="border-t px-4 py-2">
              <Link
                to="/settings/notifications"
                className="block text-center text-xs text-muted-foreground hover:text-foreground"
              >
                Notification settings
              </Link>
            </div>
          </PopoverContent>
        </Popover>

        {/* User menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-9 w-9 rounded-full">
              <Avatar className="h-9 w-9">
                <AvatarImage src={user?.avatar_url ?? undefined} alt={user?.full_name} />
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">{user?.full_name}</p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email}
                </p>
                {organization && (
                  <p className="text-xs leading-none text-muted-foreground">
                    {organization.name}
                  </p>
                )}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">Profile</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings">Settings</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={signOut} className="text-destructive">
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
